import { formatOrgTime } from "@/shared/utils/date/org-time";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";
import { X, MapPin, Camera, Maximize2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/ui/lib/utils";
import FloatingWindow from "./FloatingWindow";
import { CATEGORY_SCOPE, type MapCategory } from "@/map/config/categories";
import { AlertStatus, type Alert } from "@/alerts/types/machine-alert";
import type { Gps } from "@/machines/types/gps";
import type { Machine } from "@/machines/types/machine";
import type { MapEventRow } from "@/map/hooks/useLocationEvents";
import type { LocationAlertFilters } from "@/alerts/hooks/useLocationAlerts";
import { useMachineImages } from "@/alerts/hooks/useMachineImages";
import { MapAlertsFeed } from "./MapAlertsFeed";
import MapKpisPanel from "./MapKpisPanel";
import MapVideoMockupPanel from "./MapVideoMockupPanel";
import MapSettingsPanel, { type MapSettings } from "./MapSettingsPanel";

// Durations arrive as float seconds — cap at 2 decimals (Number() drops trailing zeros).
const fmtSeconds = (s: number) => formatNumber(Number(s.toFixed(2)));
const fmtDuration = (s: number) =>
  s >= 60
    ? `${formatNumber(Math.floor(s / 60))}m ${fmtSeconds(s % 60)}s`
    : `${fmtSeconds(s)}s`;
const fmtTime = (d: Date | string) => formatOrgTime(d, "HH:mm:ss");

const EmptyHint = ({ message }: { message: string }) => (
  <div className="py-12 text-center text-sm text-muted-foreground">{message}</div>
);

/* ----------------------------- panel contents ----------------------------- */

const EventsPanel = ({
  events,
  isLoading,
  onSelectEvent,
}: {
  events: MapEventRow[];
  isLoading: boolean;
  onSelectEvent?: (event: MapEventRow) => void;
}) => {
  const { t } = useTranslation("map");
  if (isLoading && events.length === 0)
    return <EmptyHint message={t("panel.empty.eventsLoading")} />;
  if (events.length === 0) return <EmptyHint message={t("panel.empty.events")} />;
  return (
    <div className="flex flex-col gap-2">
      {events.map((e) => (
        <button
          type="button"
          key={e.id}
          onClick={() => onSelectEvent?.(e)}
          className="rounded-lg border bg-card p-3 flex items-start gap-2.5 text-left transition-colors hover:bg-muted/50"
        >
          <span className="mt-1 h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: e.color }} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">{e.state}</span>
              <span className="text-[11px] text-muted-foreground font-mono">{formatOrgTime(e.startTime, "HH:mm")}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{e.machineName}</span>
              <span className={cn("text-[11px]", e.productive ? "text-green-600" : "text-muted-foreground")}>
                {fmtDuration(e.durationSeconds)} ·{" "}
                {e.productive ? t("events.productive") : t("events.idle")}
              </span>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};

const GpsPanel = ({ gps, color, isLoading }: { gps: Gps[]; color: string; isLoading: boolean }) => {
  const { t } = useTranslation("map");
  if (isLoading && gps.length === 0) return <EmptyHint message={t("panel.empty.gpsLoading")} />;
  if (gps.length === 0) return <EmptyHint message={t("panel.empty.gps")} />;
  const sorted = [...gps].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );
  const last = sorted[sorted.length - 1];
  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg border bg-card p-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <MapPin className="h-4 w-4" style={{ color }} />
          {t("gps.lastPosition")}
        </div>
        <div className="mt-1 font-mono text-xs text-muted-foreground">
          {last.latitude.toFixed(5)}, {last.longitude.toFixed(5)} · {fmtTime(last.timestamp)}
        </div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">{t("gps.trail")}</div>
        <div className="flex flex-col">
          {sorted.map((p, i) => (
            <div key={p.id ?? i} className="flex items-center gap-2 py-1 text-xs">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: color, opacity: 0.4 + (i / sorted.length) * 0.6 }}
              />
              <span className="font-mono text-muted-foreground">
                {p.latitude.toFixed(5)}, {p.longitude.toFixed(5)}
              </span>
              <span className="ml-auto font-mono text-muted-foreground">{fmtTime(p.timestamp)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ImagesPanel = ({
  deviceMxid,
  machineName,
  onSelectAlert,
}: {
  deviceMxid: string;
  machineName: string;
  onSelectAlert: (id: number) => void;
}) => {
  const { t } = useTranslation("map");
  const { images, isLoading } = useMachineImages(deviceMxid);
  if (isLoading && images.length === 0)
    return <EmptyHint message={t("panel.empty.imagesLoading")} />;
  if (images.length === 0) return <EmptyHint message={t("panel.empty.images")} />;
  return (
    <div className="grid grid-cols-2 gap-2">
      {images.map((img, i) => (
        <button
          key={`${img.alertId}-${i}`}
          type="button"
          onClick={() => onSelectAlert(img.alertId)}
          title={t("images.openAlert")}
          className="group relative aspect-video overflow-hidden rounded-lg border bg-muted transition-shadow hover:ring-2 hover:ring-primary"
        >
          <img
            src={img.url}
            alt={`${machineName} ${i + 1}`}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
          />
          <span className="absolute left-1.5 bottom-1.5 flex items-center gap-1 rounded bg-black/60 px-1 py-0.5 text-[9px] text-white">
            <Camera className="h-2.5 w-2.5" /> {formatOrgTime(img.timestamp, "HH:mm:ss")}
          </span>
        </button>
      ))}
    </div>
  );
};

/* ------------------------------ drawer shell ------------------------------ */

interface CategoryPanelProps {
  category: MapCategory;
  locationName: string | null;
  deviceMxid?: string;
  machineName?: string;
  machineColor?: string;
  /** Location-aggregate alerts/events (real). */
  alerts: Alert[];
  machineNameByDevice: Record<string, string>;
  alertsLoading: boolean;
  selectedAlertId: string | null;
  onSelectAlert: (id: number) => void;
  /** Global alert status filter (multi-select; empty = all). Shared with the on-map markers. */
  alertStatus: AlertStatus[];
  onAlertStatusChange: (status: AlertStatus[]) => void;
  /** Server-side alert filters (type/label/confidence) — shared with the map markers. */
  alertFilters: LocationAlertFilters;
  onAlertFiltersChange: (next: LocationAlertFilters) => void;
  /** Ids of freshly-arrived (unseen) alerts — highlighted in the feed. */
  newAlertIds?: Set<number>;
  /** Emphasized alert (hovered/selected) — other feed rows fade to half opacity. */
  emphasizedAlertId?: string | null;
  /** Feed row hover — mirrored onto the map markers by the page. */
  onHoverAlert?: (id: string | null) => void;
  events: MapEventRow[];
  eventsLoading: boolean;
  /** Event row click — opens the on-map event detail card. */
  onSelectEvent?: (event: MapEventRow) => void;
  /** Selected machine's GPS trail (real). */
  gps: Gps[];
  gpsLoading: boolean;
  /** All machines at the location (for the KPIs panel). */
  machines: Machine[];
  /** Map overlay/polygon/provider state (relocated from the floating controls). */
  mapSettings: MapSettings;
  /** Pop the drawer out into a draggable floating window. */
  floating?: boolean;
  onToggleFloat?: () => void;
  onClose: () => void;
}

const CategoryPanel = ({
  category,
  locationName,
  deviceMxid,
  machineName,
  machineColor,
  floating = false,
  onToggleFloat,
  alerts,
  machineNameByDevice,
  alertsLoading,
  selectedAlertId,
  onSelectAlert,
  alertStatus,
  onAlertStatusChange,
  alertFilters,
  onAlertFiltersChange,
  newAlertIds,
  emphasizedAlertId,
  onHoverAlert,
  events,
  eventsLoading,
  onSelectEvent,
  gps,
  gpsLoading,
  machines,
  mapSettings,
  onClose,
}: CategoryPanelProps) => {
  const { t } = useTranslation("map");
  const scope = CATEGORY_SCOPE[category];
  const categoryLabel = t(`categories.labels.${category}`);

  const renderBody = () => {
    if (scope === "machine" && !deviceMxid) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-sm text-muted-foreground">
          <MapPin className="h-6 w-6" />
          {t("panel.selectMachineHint", {
            category: t(`categories.lowerLabels.${category}`),
          })}
        </div>
      );
    }
    switch (category) {
      case "kpis":
        return <MapKpisPanel machines={machines} />;
      case "alerts":
        return (
          <MapAlertsFeed
            alerts={alerts}
            machineNameByDevice={machineNameByDevice}
            isLoading={alertsLoading}
            selectedAlertId={selectedAlertId}
            onSelectAlert={onSelectAlert}
            status={alertStatus}
            onStatusChange={onAlertStatusChange}
            filters={alertFilters}
            onFiltersChange={onAlertFiltersChange}
            newIds={newAlertIds}
            emphasizedAlertId={emphasizedAlertId}
            onHoverAlert={onHoverAlert}
          />
        );
      case "events":
        return <EventsPanel events={events} isLoading={eventsLoading} onSelectEvent={onSelectEvent} />;
      case "gps":
        return <GpsPanel gps={gps} color={machineColor || "#EF4444"} isLoading={gpsLoading} />;
      case "video":
        // Video is rendered by its own docked/floating panel, not this drawer.
        return null;
      case "video-mockup":
        return <MapVideoMockupPanel />;
      case "images":
        return deviceMxid ? (
          <ImagesPanel deviceMxid={deviceMxid} machineName={machineName ?? ""} onSelectAlert={onSelectAlert} />
        ) : null;
      case "settings":
        return <MapSettingsPanel {...mapSettings} />;
    }
  };

  const subheading =
    category === "video-mockup"
      ? t("panel.subheading.videoMockup")
      : scope === "location"
        ? locationName ?? t("panel.subheading.noLocation")
        : scope === "machine"
          ? machineName ?? t("panel.subheading.noMachine")
          : t("panel.subheading.mapSettings");

  if (floating) {
    return (
      <FloatingWindow
        title={
          <>
            <span className="font-medium">{categoryLabel}</span>
            <span className="shrink-0 truncate text-muted-foreground">· {subheading}</span>
          </>
        }
        width={360}
        bodyClassName="h-[460px] overflow-y-auto p-4"
        onDock={onToggleFloat}
        onClose={onClose}
      >
        {renderBody()}
      </FloatingWindow>
    );
  }

  return (
    <div className="absolute right-0 top-0 bottom-0 z-20 w-[340px] border-l bg-background shadow-xl flex flex-col animate-in slide-in-from-right duration-200">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div>
          <div className="font-semibold">{categoryLabel}</div>
          <div className="text-xs text-muted-foreground">{subheading}</div>
        </div>
        <div className="flex items-center gap-1">
          {onToggleFloat && (
            <button
              onClick={onToggleFloat}
              className="rounded-md p-1.5 hover:bg-muted transition-colors"
              title={t("panel.popOut")}
              aria-label={t("panel.popOut")}
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          )}
          <button onClick={onClose} className="rounded-md p-1.5 hover:bg-muted transition-colors" title={t("panel.close")} aria-label={t("panel.close")}>
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4">{renderBody()}</div>
    </div>
  );
};

export default CategoryPanel;
